import {
  Box,
  Container,
  Flex,
  Grid,
  Heading,
  Text,
  Link as RadixLink,
} from "@radix-ui/themes";
import { Link } from "react-router-dom";
import {
  CheckIcon,
  GlobeIcon,
  CameraIcon,
  ArchiveIcon,
  TargetIcon,
  ActivityLogIcon,
  UploadIcon,
  LockClosedIcon,
} from "@radix-ui/react-icons";
import { useTranslation } from "react-i18next";
import { PublicToolbar } from "../../layout/PublicPageLayout";
import { Divider } from "./components/Divider";
import { FeatureBox } from "./components/FeatureBox";
import { ContactSection } from "./components/ContactSection";
import styles from "./OnePager.module.css";
import logoSvg from "../../../../assets/logo.svg";
import asset1 from "./images/asset1.png";

export function OnePager() {
  const { t } = useTranslation();

  const features = [
    t("onePager.features.manageOcm"),
    t("onePager.features.captureProof"),
    t("onePager.features.guideVisits"),
    t("onePager.features.protectSubsidies"),
    t("onePager.features.saveTime"),
    t("onePager.features.optimizeBudget"),
  ];

  return (
    <Box className={styles.page}>
      <PublicToolbar />

      {/* Main content */}
      <Container size="4" className={styles.container}>
        <Flex px="3" width="100%">
          <Box width="100%">
            <Flex asChild direction="column" align="start" gap="4" mb="5">
              <header>
                <img src={logoSvg} alt="PromoSight" className={styles.logo} />
              </header>
            </Flex>

            <Box asChild mb="6">
              <main>
                <Flex direction={"column"} style={{ gap: "120px" }}>
                  <Flex
                    direction={{ initial: "column", sm: "row" }}
                    justify={{ initial: "start", sm: "between" }}
                    width="100%"
                    gap="4"
                  >
                    <Box flexGrow="1" flexBasis="0">
                      <Heading size="9" className={styles.title}>
                        {t("onePager.title")}
                      </Heading>

                      <Text
                        as="p"
                        size="6"
                        color="gray"
                        className={styles.tagline}
                      >
                        {t("onePager.tagline")}
                      </Text>

                      <Divider />

                      <Flex direction="column" gap="3" mt="4">
                        {features.map((feature) => (
                          <Flex key={feature} gap="2" align="center">
                            <CheckIcon
                              width="20"
                              height="20"
                              color="var(--accent-9)"
                            />
                            <Text size="4">{feature}</Text>
                          </Flex>
                        ))}
                      </Flex>
                    </Box>
                    <Flex
                      flexGrow={{ initial: "0", sm: "1" }}
                      flexBasis="0"
                      justify={{ initial: "start", sm: "end" }}
                    >
                      <img
                        src={asset1}
                        alt="PromoSight Asset"
                        style={{
                          width: "auto",
                          height: "auto",
                          maxHeight: "400px",
                          maxWidth: "100%",
                        }}
                      />
                    </Flex>
                  </Flex>

                  <Box>
                    <Box style={{ textAlign: "center" }}>
                      <Heading weight="medium" size="6" color="gray">
                        {t("onePager.solution.label")}
                      </Heading>
                      <Heading weight="medium" size="8" highContrast>
                        {t("onePager.solution.title")}
                      </Heading>
                      <Flex justify="center" style={{ padding: "2em 0" }}>
                        <Box className={styles.solutionTextWrapper}>
                          <Text>{t("onePager.solution.description")}</Text>
                        </Box>
                      </Flex>
                    </Box>

                    <Flex
                      direction={{ initial: "column", sm: "row" }}
                      gap="3"
                      width="100%"
                    >
                      <FeatureBox
                        title={t("onePager.mainFeatures.guidedVisits.title")}
                        icon={<GlobeIcon width="24" height="24" />}
                      >
                        {t("onePager.mainFeatures.guidedVisits.description")}
                      </FeatureBox>
                      <FeatureBox
                        title={t("onePager.mainFeatures.structuredProof.title")}
                        icon={<CameraIcon width="24" height="24" />}
                      >
                        {t("onePager.mainFeatures.structuredProof.description")}
                      </FeatureBox>
                      <FeatureBox
                        title={t("onePager.mainFeatures.auditReadyPacks.title")}
                        icon={<ArchiveIcon width="24" height="24" />}
                      >
                        {t("onePager.mainFeatures.auditReadyPacks.description")}
                      </FeatureBox>
                    </Flex>
                  </Box>

                  <Box>
                    <Heading size="7">
                      {t("onePager.keyCapabilities.title")}
                    </Heading>
                    <Divider />

                    <Grid
                      columns={{ initial: "1", sm: "3" }}
                      gap="3"
                      width="100%"
                      style={{ paddingTop: "32px" }}
                    >
                      <FeatureBox
                        title={t(
                          "onePager.keyCapabilities.structuredProofCapture.title",
                        )}
                        icon={<CameraIcon width="24" height="24" />}
                        variant="light"
                      >
                        {t(
                          "onePager.keyCapabilities.structuredProofCapture.description",
                        )}
                      </FeatureBox>
                      <FeatureBox
                        title={t(
                          "onePager.keyCapabilities.intelligentStoreGuidance.title",
                        )}
                        icon={<TargetIcon width="24" height="24" />}
                        variant="light"
                      >
                        {t(
                          "onePager.keyCapabilities.intelligentStoreGuidance.description",
                        )}
                      </FeatureBox>
                      <FeatureBox
                        title={t(
                          "onePager.keyCapabilities.instantAuditPack.title",
                        )}
                        icon={<ArchiveIcon width="24" height="24" />}
                        variant="light"
                      >
                        {t(
                          "onePager.keyCapabilities.instantAuditPack.description",
                        )}
                      </FeatureBox>
                      <FeatureBox
                        title={t(
                          "onePager.keyCapabilities.monitorOcmBudget.title",
                        )}
                        icon={<ActivityLogIcon width="24" height="24" />}
                        variant="light"
                      >
                        {t(
                          "onePager.keyCapabilities.monitorOcmBudget.description",
                        )}
                      </FeatureBox>
                      <FeatureBox
                        title={t(
                          "onePager.keyCapabilities.flexibleDataImport.title",
                        )}
                        icon={<UploadIcon width="24" height="24" />}
                        variant="light"
                      >
                        {t(
                          "onePager.keyCapabilities.flexibleDataImport.description",
                        )}
                      </FeatureBox>
                      <FeatureBox
                        title={t(
                          "onePager.keyCapabilities.governanceAccess.title",
                        )}
                        icon={<LockClosedIcon width="24" height="24" />}
                        variant="light"
                      >
                        {t(
                          "onePager.keyCapabilities.governanceAccess.description",
                        )}
                      </FeatureBox>
                    </Grid>
                  </Box>

                  {/* Contact Section */}
                  <Box style={{ maxWidth: "600px", margin: "0 auto" }}>
                    <ContactSection />
                  </Box>
                </Flex>
              </main>
            </Box>
          </Box>
        </Flex>
      </Container>

      <Box py="3">
        <Flex justify="center" align="center" gap="2">
          <Text size="1" style={{ color: "var(--gray-9)" }}>
            <RadixLink asChild style={{ color: "inherit" }}>
              <Link to="/privacy-policy">
                {t("cookieConsent.privacyPolicyLink")}
              </Link>
            </RadixLink>
          </Text>
          <Text size="1" style={{ color: "var(--gray-9)" }}>
            |
          </Text>
          <Text size="1" style={{ color: "var(--gray-9)" }}>
            <RadixLink asChild style={{ color: "inherit" }}>
              <Link to="/terms-of-use">
                {t("cookieConsent.termsOfUseLink")}
              </Link>
            </RadixLink>
          </Text>
          <Text size="1" style={{ color: "var(--gray-9)" }}>
            |
          </Text>
          <Text size="1" style={{ color: "var(--gray-9)" }}>
            © {new Date().getFullYear()}.{" "}
            <RadixLink
              href="https://www.bec.dev"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit" }}
            >
              Bec Développement & Solutions inc.
            </RadixLink>
          </Text>
        </Flex>
      </Box>
    </Box>
  );
}
